import { useMemo } from 'react'
import useAppStore from '$appStore'
import { CompanySettings, ILoadedVenue } from '$models'
import {
  getActiveCompanySettings,
  PermissionType,
  venueHasPermissions,
} from '$utils'
import { useCompanySettings } from '$hooks'

/**
 * What the staff member pages let the current user see
 *
 * Time is a given: staff members live under the Time module in the top nav.
 */
export interface StaffMemberAccess {
  /**
   * Time and Payroll at the active venue. With a payroll venue that is not the active one,
   * also Payroll there, matching what the api checks at the paying venue
   */
  readonly canViewPayRates: (payrollVenueId?: string | null) => boolean
}

/**
 * Reads the access flags outside of React, for loaders and store methods.
 */
export const getStaffMemberAccess = (): StaffMemberAccess => {
  const { activeCompany, venues } = useAppStore.getState()

  return buildStaffMemberAccess(
    getActiveCompanySettings(),
    venues,
    activeCompany?.id
  )
}

/**
 * Reads the access flags in a component, refreshing when the active company changes.
 */
export const useStaffMemberAccess = (): StaffMemberAccess => {
  const settings = useCompanySettings()
  const venues = useAppStore((state) => state.venues)
  const activeCompanyId = useAppStore((state) => state.activeCompany?.id)

  return useMemo(
    () => buildStaffMemberAccess(settings, venues, activeCompanyId),
    [settings, venues, activeCompanyId]
  )
}

export const buildStaffMemberAccess = (
  settings: CompanySettings,
  venues: ILoadedVenue[],
  activeCompanyId?: string
): StaffMemberAccess => {
  const { hasTimePermission, hasPayrollPermission } = settings

  const canViewActiveVenuePayRates = hasTimePermission && hasPayrollPermission

  return {
    canViewPayRates: (payrollVenueId?: string | null) => {
      if (!canViewActiveVenuePayRates) return false

      if (!payrollVenueId || payrollVenueId === activeCompanyId) return true

      return venueHasPermissions(venues, payrollVenueId, [
        PermissionType.Payroll,
      ])
    },
  }
}
